import React from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuClock, LuDownload, LuEye, LuFileText, LuFileUp, LuInfo, LuPencil, LuPlus, LuReceipt, LuSend, LuWallet, LuX } from 'react-icons/lu';
import Badge from '../../../../../components/ui/Badge';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { Abs, Btn, CARD, Dialog, DialogIcon, Field, Note, SectionHead, Segmented, TILE } from '../kit';

// ── Masraf Talep Et ─────────────────────────────────────────────────────
export const EXPENSE = (top) => ({
  card: { x: 256, y: top + 60, w: 1000, h: 440 },
  amount: { x: 280, y: top + 106, w: 470, h: 42 },
  category: { x: 762, y: top + 106, w: 470, h: 42 },
  description: { x: 280, y: top + 182, w: 952, h: 72 },
  drop: { x: 280, y: top + 290, w: 952, h: 74 },
  files: { x: 280, y: top + 374, w: 952, h: 36 },
  note: { x: 280, y: top + 430, w: 700, h: 52 },
  submit: { x: 1000, y: top + 436, w: 232, h: 42 },
  success: { x: 256, y: top + 60, w: 1000, h: 250 },
  goTracking: { x: 840, y: top + 250, w: 170, h: 40 },
  newRequest: { x: 1020, y: top + 250, w: 212, h: 40 },
});

/** state: { filled (0-3), focus, files, pressed, sending, success } */
export const ExpenseFormPane = ({ top, state }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.expense.${key}`, vars);
  const E = EXPENSE(top);
  const filled = state.filled || 0;
  return (
    <>
      <SectionHead top={top} icon={LuWallet} gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" title={label('form.title')} description={label('form.description')} />
      {state.success ? (
        <>
          <Abs rect={E.success} className={`tw-flex tw-flex-col tw-items-center tw-pt-8 tw-text-center ${CARD}`}>
            <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-full tw-bg-emerald-500 tw-text-white tw-shadow-lg tw-shadow-emerald-500/30"><LuCircleCheck aria-hidden="true" className="tw-h-8 tw-w-8" /></span>
            <p className="tw-mt-3 tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('form.successTitle')}</p>
            <p className="tw-mt-1 tw-max-w-xl tw-text-sm tw-text-slate-500">{label('form.successText')}</p>
          </Abs>
          <Btn rect={E.goTracking}>{label('form.goTracking')}</Btn>
          <Btn rect={E.newRequest} variant="primary" icon={LuPlus}>{label('form.newRequest')}</Btn>
        </>
      ) : (
        <>
          <Abs rect={E.card} className={CARD} />
          <Field rect={E.amount} label={label('form.amount')} required prefix="₺" value={filled >= 1 ? '850,00' : ''} placeholder="0.00" focused={state.focus === 'amount'} />
          <Field rect={E.category} label={label('form.category')} required select value={filled >= 1 ? label('categories.transport') : label('categories.other')} focused={state.focus === 'category'} />
          <Field rect={E.description} label={label('form.descriptionLabel')} required area value={filled >= 2 ? t('userGuide.stage.expenseDesc') : ''} placeholder={label('form.descriptionPlaceholder')} focused={state.focus === 'description'} />
          <span style={{ left: 282, top: top + 268 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('form.documents')} <span className="tw-font-normal tw-text-slate-400">· {label('form.documentsHint')}</span></span>
          <Abs rect={E.drop} className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-2xl tw-border-2 tw-border-dashed tw-transition-colors tw-duration-300 ${state.focus === 'drop' ? 'tw-border-teal-500 tw-bg-teal-500/[0.06]' : 'tw-border-slate-300 tw-bg-slate-50/60 dark:tw-border-white/15 dark:tw-bg-white/[0.02]'}`}>
            <LuFileUp aria-hidden="true" className="tw-h-5 tw-w-5 tw-text-teal-600" />
            <span className="tw-mt-1 tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{label('pdf.choose')}</span>
            <span className="tw-text-[11px] tw-text-slate-500">{label('pdf.limits')}</span>
          </Abs>
          {(state.files || 0) > 0 && (
            <Abs rect={E.files} className="tw-flex tw-items-center tw-gap-2">
              {['fis-taksi.pdf', 'otel-fatura.pdf'].slice(0, state.files).map((name) => (
                <span key={name} className="tw-inline-flex tw-h-8 tw-items-center tw-gap-2 tw-rounded-full tw-bg-rose-500/10 tw-px-3 tw-text-xs tw-font-semibold tw-text-rose-700">
                  <LuFileText aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />{name}<span className="tw-font-normal tw-text-rose-500">0,4 MB</span><LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                </span>
              ))}
            </Abs>
          )}
          <Note rect={E.note} icon={LuInfo}>{label('form.approvalNote')}</Note>
          <Btn rect={E.submit} variant="primary" icon={LuSend} pressed={state.pressed} busy={state.sending}>{state.sending ? label('form.sending') : label('form.submit')}</Btn>
        </>
      )}
    </>
  );
};

ExpenseFormPane.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired };

// ── Masraf Takip ────────────────────────────────────────────────────────
export const EXPENSE_TRACK = (top) => ({
  newRequest: { x: 1132, y: top + 4, w: 124, h: 34 },
  stats: { x: 256, y: top + 66, w: 1000, h: 62 },
  stat: (index) => ({ x: 256 + index * 254, y: top + 66, w: 242, h: 62 }),
  filter: { x: 256, y: top + 142, w: 404, h: 36 },
  filterWidths: [72, 96, 108, 112],
  card: (index) => ({ x: 256 + (index % 2) * 506, y: top + 192 + Math.floor(index / 2) * 176, w: 494, h: 164 }),
  edit: (index) => {
    const card = { x: 256 + (index % 2) * 506, y: top + 192 + Math.floor(index / 2) * 176, w: 494, h: 164 };
    return { x: card.x + card.w - 124, y: card.y + card.h - 46, w: 108, h: 32 };
  },
});

const EXPENSES = [
  { amount: '₺850', status: 'pending', category: 'transport', date: '11.10.2026', desc: 'expenseDesc', docs: 2 },
  { amount: '₺1.240', status: 'approved', category: 'accommodation', date: '28.09.2026', desc: 'expenseDesc2', docs: 1, processed: '30.09.2026' },
  { amount: '₺320', status: 'rejected', category: 'food', date: '14.09.2026', desc: 'expenseDesc3', docs: 1, reason: 'expenseReject' },
];
const EXPENSE_TONE = { pending: ['warning', LuClock], approved: ['success', LuCircleCheck], rejected: ['danger', LuX] };

export const ExpenseTrackingPane = ({ top, state }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.expense.${key}`, vars);
  const E = EXPENSE_TRACK(top);
  const filter = state.filter || 'all';
  const rows = filter === 'all' ? EXPENSES : EXPENSES.filter((row) => row.status === filter);
  const stats = [['total', 3, 'tw-text-slate-900 dark:tw-text-white'], ['pending', 1, 'tw-text-amber-600'], ['approved', 1, 'tw-text-emerald-600'], ['rejected', 1, 'tw-text-rose-600']];
  return (
    <>
      <SectionHead top={top} icon={LuReceipt} gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" title={label('tracking.title')} description={label('tracking.description')} />
      <Btn rect={E.newRequest} icon={LuPlus} className="tw-text-xs">{label('tracking.newRequest')}</Btn>
      {stats.map(([key, value, tone], index) => (
        <Abs key={key} rect={E.stat(index)} className={`tw-px-4 tw-py-2 ${TILE}`}>
          <p className={`tw-text-2xl tw-font-semibold tw-tabular-nums ${tone}`}>{value}</p>
          <p className="tw-text-xs tw-text-slate-500">{key === 'total' ? label('tracking.total') : label(`status.${key}`)}</p>
        </Abs>
      ))}
      <Segmented
        rect={E.filter}
        value={filter}
        small
        options={['all', 'pending', 'approved', 'rejected'].map((key, index) => ({ value: key, label: key === 'all' ? label('tracking.all') : label(`status.${key}`), w: E.filterWidths[index] }))}
      />
      {rows.map((row, index) => {
        const [tone, Icon] = EXPENSE_TONE[row.status];
        return (
          <Abs key={row.amount} rect={E.card(index)} className={`tw-flex tw-flex-col tw-p-4 ${CARD} ${state.highlight === row.status ? 'tw-ring-2 tw-ring-teal-400/60' : ''}`}>
            <div className="tw-flex tw-items-start tw-justify-between">
              <p className="tw-text-2xl tw-font-bold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{row.amount}</p>
              <Badge tone={tone}><Icon aria-hidden="true" className="tw-h-3 tw-w-3" />{label(`status.${row.status}`)}</Badge>
            </div>
            <p className="tw-mt-1 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500">
              <span className="tw-rounded-full tw-bg-slate-900/[0.05] tw-px-2 tw-py-0.5 tw-font-semibold tw-text-slate-600 dark:tw-bg-white/10 dark:tw-text-slate-300">{label(`categories.${row.category}`)}</span>
              {row.date}
            </p>
            <p className="tw-mt-1.5 tw-truncate tw-text-[13px] tw-text-slate-700 dark:tw-text-slate-200">{t(`userGuide.stage.${row.desc}`)}</p>
            {row.reason && (
              <p className="tw-mt-1.5 tw-truncate tw-rounded-lg tw-bg-rose-500/10 tw-px-2 tw-py-1 tw-text-[11px] tw-text-rose-800 dark:tw-text-rose-200"><b>{label('tracking.rejectionReason')}:</b> {t(`userGuide.stage.${row.reason}`)}</p>
            )}
            <p className="tw-mt-auto tw-text-[11px] tw-text-slate-500">{row.status === 'pending' ? label('tracking.pendingHint') : label('tracking.processedOn', { date: row.processed || '16.09.2026' })}</p>
          </Abs>
        );
      })}
      {rows.map((row, index) => (row.status === 'pending' ? <Btn key="edit" rect={E.edit(index)} icon={LuPencil} pressed={state.editPressed} className="tw-text-xs">{label('tracking.edit')}</Btn> : null))}
    </>
  );
};

ExpenseTrackingPane.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired };

export const EXPENSE_EDIT = {
  dialog: { x: 352, y: 56, w: 576, h: 608 },
  amount: { x: 376, y: 172, w: 256, h: 42 },
  category: { x: 648, y: 172, w: 256, h: 42 },
  description: { x: 376, y: 252, w: 528, h: 66 },
  existing: { x: 376, y: 340, w: 528, h: 96 },
  replace: { x: 376, y: 470, w: 528, h: 64 },
  save: { x: 808, y: 612, w: 96, h: 38 },
};

export const ExpenseEditDialog = ({ appear }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.expense.${key}`, vars);
  return (
    <Dialog rect={EXPENSE_EDIT.dialog} appear={appear} footer title={label('edit.title')} subtitle={label('edit.subtitle')} icon={<DialogIcon icon={LuPencil} gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" />}>
      <Field rect={EXPENSE_EDIT.amount} label={label('form.amount')} prefix="₺" value="850,00" />
      <Field rect={EXPENSE_EDIT.category} label={label('form.category')} select value={label('categories.transport')} />
      <Field rect={EXPENSE_EDIT.description} label={label('form.descriptionLabel')} area value={t('userGuide.stage.expenseDesc')} />
      <Abs rect={EXPENSE_EDIT.existing} className={`tw-p-3 ${TILE}`}>
        <p className="tw-flex tw-justify-between tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('edit.existing')}<span className="tw-text-rose-600">{label('edit.removeExisting')}</span></p>
        {['fis-taksi.pdf', 'otel-fatura.pdf'].map((name) => (
          <p key={name} className="tw-mt-2 tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-text-slate-700 dark:tw-text-slate-200">
            <LuFileText aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-rose-500" /><span className="tw-flex-1">{name}</span>
            <LuEye aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" /><LuDownload aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
          </p>
        ))}
      </Abs>
      <span style={{ left: 378, top: 448 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('edit.replace')} <span className="tw-font-normal tw-text-slate-400">· {label('edit.replaceHint')}</span></span>
      <Abs rect={EXPENSE_EDIT.replace} className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-slate-300 tw-text-sm tw-font-semibold tw-text-slate-600 dark:tw-border-white/15 dark:tw-text-slate-300">
        <LuFileUp aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600" />{label('pdf.choose')}
      </Abs>
      <Btn rect={{ ...EXPENSE_EDIT.save, x: EXPENSE_EDIT.save.x - 104 }} variant="ghost">{t('profilePage.common.cancel')}</Btn>
      <Btn rect={EXPENSE_EDIT.save} variant="primary">{t('profilePage.common.save')}</Btn>
    </Dialog>
  );
};

ExpenseEditDialog.propTypes = { appear: PropTypes.number };

